import { useState, useRef, useEffect } from "react";
import {
  useContentStore,
  forceBroadcastUpdate,
  getContent,
  type BannerSlide,
  type AboutStat,
  type ProductItem,
  type ContactInfoItem,
  type NavItem,
  type FriendLink,
  type SocialLink,
  type NewsArticle,
} from "@/hooks/useContentStore";
import { API_ENDPOINTS } from "@/lib/api";
import {
  broadcastImageUpdate,
} from "@/hooks/useImageStore";
import ProductDetailEditorSection from "@/pages/admin/components/ProductDetailEditor";

type Section = "nav" | "banner" | "about" | "products" | "contact" | "productsDetail" | "news" | "friendLinks";

const sectionPreviewPath: Record<Section, string> = {
  nav: "/",
  banner: "/#banner",
  about: "/#about",
  products: "/#products",
  contact: "/#contact",
  productsDetail: "/products",
  news: "/news",
  friendLinks: "/#footer",
};

const sectionHelp: Record<Section, string> = {
  nav: "编辑 Logo、导航菜单文字、跳转链接和中英文标签。",
  banner: "编辑首页首屏轮播的标题、副标题、背景图片和视频。",
  about: "编辑公司介绍、能力数据和关于我们配图。",
  products: "编辑首页产品服务卡片，影响首页产品展示入口。",
  contact: "编辑联系信息、地图嵌入地址和联系模块标题。",
  productsDetail: "编辑产品列表页和详情页的产品资料、参数、图片和分类。",
  news: "编辑新闻发布栏目，支持高级文档形式发布图文视频新闻事件。",
  friendLinks: "编辑页脚友情链接、社交图标、版权备案和背景音乐。",
};

// ─── Shared primitives ─────────────────────────────────────────────────────

function FieldInput({
  label,
  value,
  onChange,
  multiline = false,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[#8aaec8] text-xs">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          placeholder={placeholder}
          className="bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs resize-none focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
        />
      )}
    </div>
  );
}

type ImageUploadStatus = "idle" | "uploading" | "success" | "error";

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("文件读取失败"));
    reader.readAsDataURL(file);
  });
}

function getVideoMimeType(fileNameOrUrl: string, fileType?: string): string {
  if (fileType) return fileType;
  const ext = fileNameOrUrl.split("?")[0].split("#")[0].split(".").pop()?.toLowerCase() ?? "";
  if (ext === "mp4" || ext === "m4v" || ext === "mov") return "video/mp4";
  if (ext === "webm") return "video/webm";
  if (ext === "ogg" || ext === "ogv") return "video/ogg";
  if (ext === "wmv") return "video/x-ms-wmv";
  if (ext === "avi") return "video/x-msvideo";
  if (ext === "mkv") return "video/x-matroska";
  if (ext === "flv") return "video/x-flv";
  return "video/mp4";
}

function buildVideoPreviewHtml(rawUrl: string): string {
  const url = rawUrl.trim();
  const m3u8 = url.match(/\.m3u8(?:\?.*)?$/i);
  const directVideo = url.match(/\.(mp4|webm|ogg|mov|m4v|wmv|avi|mkv|flv|ogv)(?:\?.*)?$/i);
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  if (yt) {
    return `<iframe src="https://www.youtube.com/embed/${yt[1]}" style="width:100%;height:100%;border:0;display:block;background:#000;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen data-native-video="1"></iframe>`;
  }
  const bili = url.match(/bilibili\.com\/video\/(BV[0-9A-Za-z]+)/);
  if (bili) {
    return `<iframe src="https://player.bilibili.com/player.html?bvid=${bili[1]}&page=1" style="width:100%;height:100%;border:0;display:block;background:#000;" allowfullscreen data-native-video="1"></iframe>`;
  }
  if (m3u8) {
    return `<video controls preload="metadata" playsinline webkit-playsinline x5-playsinline src="${url}" style="width:100%;height:100%;display:block;background:#000;object-fit:contain;" data-native-video="1"></video>`;
  }
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) {
    return `<iframe src="https://player.vimeo.com/video/${vimeo[1]}" style="width:100%;height:100%;border:0;display:block;background:#000;" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen data-native-video="1"></iframe>`;
  }
  if (directVideo) {
    return `<video controls preload="metadata" playsinline webkit-playsinline x5-playsinline crossorigin="anonymous" style="width:100%;height:100%;display:block;background:#000;object-fit:contain;" data-native-video="1"><source src="${url}" type="${getVideoMimeType(url)}" />你的浏览器暂不支持此视频播放。</video>`;
  }
  return `<video controls preload="metadata" playsinline webkit-playsinline x5-playsinline src="${url}" style="width:100%;height:100%;display:block;background:#000;object-fit:contain;" data-native-video="1"></video>`;
}

async function uploadMediaFile(file: File, category: "image" | "video") {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", category);
  const res = await fetch(API_ENDPOINTS.uploadMedia, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "上传失败，请检查 PHP 接口和目录权限");
  }
  const data = (await res.json()) as { url?: string; absolute_url?: string; error?: string };
  const nextUrl = data.url?.trim() || data.absolute_url?.trim();
  if (!nextUrl) throw new Error(data.error || "上传成功但未返回可用地址");
  return nextUrl;
}

function buildVideoFigureHtml(url: string) {
  return buildVideoPreviewHtml(url);
}

function ImageField({
  label,
  value,
  onChange,
  aspectRatio = "16/9",
  storageKey,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  aspectRatio?: "16/9" | "4/3" | "1/1" | "21/9";
  storageKey: string;
}) {
  const [preview, setPreview] = useState(value);
  const [inputVal, setInputVal] = useState(value);
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imgSize, setImgSize] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<ImageUploadStatus>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync preview when value changes externally
  useEffect(() => {
    setPreview(value);
    setInputVal(value);
  }, [value]);

  const handleApply = () => {
    setErrored(false);
    setLoading(true);
    setPreview(inputVal);
    onChange(inputVal);
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setLoading(false);
    const img = e.currentTarget;
    setImgSize(`${img.naturalWidth} × ${img.naturalHeight}`);
  };

  const handleImageError = () => {
    setLoading(false);
    setErrored(true);
    setImgSize("");
  };

  const handleCloudUpload = async (file: File) => {
    if (!file.type.startsWith("image/") && !file.name.match(/\.(jpg|jpeg|png|webp|gif|avif|svg)$/i)) {
      setUploadError("不支持的文件格式，请上传 JPG / PNG / WebP / GIF");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setUploadError("文件大小不能超过 8MB，建议先压缩图片后再上传");
      return;
    }

    setUploadStatus("uploading");
    setUploadError(null);
    setUploadedFileName(file.name);
    setUploadProgress(0);

    const progressInterval = setInterval(() => {
      setUploadProgress((p) => Math.min(p + Math.random() * 20, 85));
    }, 200);

    try {
      const dataUrl = await fileToDataUrl(file);
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStatus("success");

      setInputVal(dataUrl);
      setPreview(dataUrl);
      setErrored(false);
      onChange(dataUrl);
      broadcastImageUpdate(storageKey);
    } catch (err: unknown) {
      clearInterval(progressInterval);
      setUploadStatus("error");
      setUploadError(err instanceof Error ? err.message : "上传失败，请重试");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleCloudUpload(file);
  };

  const ratioClass = {
    "21/9": "aspect-[21/9]",
    "16/9": "aspect-video",
    "4/3": "aspect-[4/3]",
    "1/1": "aspect-square",
  }[aspectRatio];

  const isEmpty = !preview || errored;
  const isEmbeddedUrl = value?.startsWith("data:") ?? false;

  return (
    <div className="bg-[#0a1628] border border-[#1a2840] rounded-xl overflow-hidden">
      {/* Preview area */}
      <div
        className={`relative w-full ${ratioClass} bg-[#060a12] overflow-hidden group ${
          dragOver ? "ring-2 ring-[#4fc3f7]/50" : ""
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {uploadStatus === "uploading" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#060a12]">
            <i className="ri-loader-4-line text-[#4fc3f7] text-3xl animate-spin" />
            <div className="w-48">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[#8aaec8] text-xs truncate max-w-[140px]">{uploadedFileName}</span>
                <span className="text-[#4fc3f7] text-xs font-mono">{Math.round(uploadProgress)}%</span>
              </div>
              <div className="w-full h-1.5 bg-[#1e2d4a] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#4fc3f7] to-cyan-400 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-[#5a7a9a] text-[10px] mt-1 text-center">正在写入本地素材...</p>
            </div>
          </div>
        ) : !isEmpty ? (
          <>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-[#060a12]">
                <i className="ri-loader-4-line text-[#4fc3f7] text-2xl animate-spin" />
              </div>
            )}
            <img
              src={preview}
              alt={label}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {/* Overlay info bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 py-2.5 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-[10px] font-mono">{imgSize}</span>
                {isEmbeddedUrl && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] border border-emerald-500/30">
                    本地
                  </span>
                )}
              </div>
              <a
                href={preview}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4fc3f7] text-[10px] hover:text-white transition-colors flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="ri-external-link-line" />
                查看原图
              </a>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#3a5a7a]">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d1529] border border-[#1e2d4a]">
              <i className="ri-image-line text-xl" />
            </div>
            <span className="text-xs">{errored ? "图片加载失败，请检查链接" : "暂无图片预览"}</span>
            <span className="text-[10px] text-[#2a4a6a]">拖拽图片到此处或点击下方上传</span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-white text-xs font-semibold">{label}</p>
          {isEmbeddedUrl && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px]">
              <i className="ri-hard-drive-2-line text-[10px]" />
              已内嵌本地
            </span>
          )}
        </div>

        {/* Cloud upload drop zone */}
        <div
          className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer ${
            dragOver
              ? "border-[#4fc3f7]/70 bg-[#4fc3f7]/8"
              : uploadStatus === "success" || isEmbeddedUrl
              ? "border-emerald-500/40 bg-emerald-500/5"
              : uploadStatus === "error"
              ? "border-red-500/40 bg-red-500/5"
              : "border-[#1e2d4a] hover:border-[#4fc3f7]/40 bg-[#070c1a]"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => uploadStatus !== "uploading" && fileInputRef.current?.click()}
        >
          {uploadStatus === "uploading" ? (
            <div className="p-4 flex items-center gap-3">
              <i className="ri-loader-4-line text-[#4fc3f7] text-xl animate-spin flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#8aaec8] text-xs truncate max-w-[160px]">{uploadedFileName}</span>
                  <span className="text-[#4fc3f7] text-xs font-mono">{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full h-1 bg-[#1e2d4a] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#4fc3f7] to-cyan-400 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (uploadStatus === "success" || isEmbeddedUrl) && value ? (
            <div className="p-3 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex-shrink-0">
                <i className="ri-image-fill text-emerald-400 text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate">
                  {uploadedFileName || value.split("/").pop()?.replace(/^site_img_\d+_[a-z0-9]+\./, "图片.") || "图片文件"}
                </p>
                <span className="flex items-center gap-1 text-emerald-400 text-[10px] mt-0.5">
                  <i className="ri-hard-drive-2-line" />
                  已写入内容数据，保存后随数据库生效
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadStatus("idle");
                  setUploadedFileName(null);
                  fileInputRef.current?.click();
                }}
                className="flex-shrink-0 px-2 py-1 rounded-lg text-[#5a7a9a] hover:text-[#4fc3f7] hover:bg-[#4fc3f7]/10 transition-colors cursor-pointer text-xs whitespace-nowrap"
              >
                重新上传
              </button>
            </div>
          ) : (
            <div className="p-4 flex flex-col items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0d1529] border border-[#1e2d4a]">
                <i className="ri-upload-cloud-2-line text-[#4fc3f7] text-xl" />
              </div>
              <div className="text-center">
                <p className="text-[#8aaec8] text-xs">拖拽图片到此处，或点击写入本地素材</p>
                <p className="text-[#5a7a9a] text-[10px] mt-0.5">JPG / PNG / WebP / GIF · 最大 8MB</p>
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {uploadError && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
            <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0" />
            <span className="text-red-400 text-xs">{uploadError}</span>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleCloudUpload(file);
            e.target.value = "";
          }}
        />

        {/* Divider */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-[1px] bg-[#1e2d4a]" />
          <span className="text-[#3a5a7a] text-[10px]">或手动填写图片链接</span>
          <div className="flex-1 h-[1px] bg-[#1e2d4a]" />
        </div>

        {/* URL input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleApply()}
            placeholder="粘贴图片 URL..."
            className="flex-1 min-w-0 bg-[#070c1a] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
          />
          <button
            onClick={handleApply}
            className="px-3 py-2 rounded-lg bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] text-xs font-semibold hover:bg-[#4fc3f7]/25 transition-colors cursor-pointer whitespace-nowrap"
          >
            应用
          </button>
          {inputVal && (
            <button
              onClick={() => { setInputVal(""); setPreview(""); setErrored(false); onChange(""); }}
              className="px-2 py-2 rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] transition-colors cursor-pointer"
              title="清空"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        {errored && (
          <p className="text-red-400 text-[10px] flex items-center gap-1">
            <i className="ri-error-warning-line" />
            无法加载该图片，请检查 URL 是否可访问
          </p>
        )}

        <p className="text-[#5a7a9a] text-[10px]">
          <i className="ri-information-line mr-1 text-emerald-400" />
          图片会内嵌到内容数据中。使用 Node.js 部署时，点击「保存内容」后所有访客可见。
        </p>
      </div>
    </div>
  );
}

function SectionTab({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
        active
          ? "bg-[#4fc3f7]/15 text-[#4fc3f7] border border-[#4fc3f7]/30"
          : "text-[#5a7a9a] hover:text-white border border-transparent"
      }`}
    >
      <i className={icon}></i>
      {label}
    </button>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-[#4fc3f7]/40 text-[#4fc3f7] text-sm hover:bg-[#4fc3f7]/10 transition-colors cursor-pointer whitespace-nowrap w-full justify-center"
    >
      <i className="ri-add-line text-base"></i>
      {label}
    </button>
  );
}

function DeleteButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "至少保留一条" : "删除此条目"}
      className={`flex items-center justify-center w-7 h-7 rounded-lg transition-colors cursor-pointer ${
        disabled ? "text-[#2a3a4a] cursor-not-allowed" : "text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d]"
      }`}
    >
      <i className="ri-delete-bin-line text-sm"></i>
    </button>
  );
}

// ─── Nav Section ───────────────────────────────────────────────────────────

function NavSection() {
  const { content, updateNavField, updateNavItem, addNavItem, removeNavItem } = useContentStore();
  const { nav } = content;

  return (
    <div className="space-y-4">
      {/* Logo */}
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-image-line text-[#4fc3f7]"></i>
          Logo 图片
        </p>
        <ImageField
          label="Logo 图片"
          value={nav.logoImage}
          onChange={(v) => updateNavField("logoImage", v)}
          aspectRatio="1/1"
          storageKey="nav.logoImage"
        />
        <div className="mt-4">
          <FieldInput
            label="Logo Alt 文字（图片无法加载时显示）"
            value={nav.logoAlt}
            onChange={(v) => updateNavField("logoAlt", v)}
            placeholder="如：公司名称"
          />
        </div>
        <div className="mt-4">
          <FieldInput
            label="OG 标题（首页社交分享标题）"
            value={nav.ogTitle}
            onChange={(v) => updateNavField("ogTitle", v)}
            placeholder="如：航空发动机 | 内燃发动机·无人机动力·涡喷发动机·精密制造·"
          />
        </div>
      </div>

      {/* Nav Items */}
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-menu-line text-[#4fc3f7]"></i>
          导航菜单项
          <span className="ml-auto text-[#5a7a9a] text-xs font-normal">共 {nav.items.length} 项</span>
        </p>
        <div className="space-y-3 mb-4">
          {nav.items.map((item: NavItem, i: number) => (
            <div key={i} className="bg-[#0a1628] border border-[#1a2840] rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[#5a7a9a] text-xs flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center rounded bg-[#4fc3f7]/10 text-[#4fc3f7] text-xs font-bold">
                    {i + 1}
                  </span>
                  菜单项 {i + 1}
                  {!item.scroll && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 text-[10px] border border-orange-500/20">
                      跳转页面
                    </span>
                  )}
                  {item.scroll && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] border border-emerald-500/20">
                      锚点滚动
                    </span>
                  )}
                </span>
                <DeleteButton onClick={() => removeNavItem(i)} disabled={nav.items.length <= 1} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldInput
                  label="中文名称"
                  value={item.zh}
                  onChange={(v) => updateNavItem(i, "zh", v)}
                  placeholder="如：首页"
                />
                <FieldInput
                  label="英文名称"
                  value={item.en}
                  onChange={(v) => updateNavItem(i, "en", v)}
                  placeholder="e.g. Home"
                />
              </div>
              <div className="mt-3">
                <FieldInput
                  label="链接地址（锚点用 #xxx，页面用 /path）"
                  value={item.href}
                  onChange={(v) => updateNavItem(i, "href", v)}
                  placeholder="如：#banner 或 /products"
                />
              </div>
            </div>
          ))}
        </div>
        <AddButton label="添加菜单项" onClick={addNavItem} />
        <p className="mt-3 text-[#5a7a9a] text-xs leading-relaxed">
          <i className="ri-information-line mr-1 text-[#4fc3f7]"></i>
          链接以 <code className="text-[#4fc3f7]">#</code> 开头表示锚点滚动（同页跳转），以 <code className="text-[#4fc3f7]">/</code> 开头表示跳转到新页面。
        </p>
      </div>
    </div>
  );
}

// ─── Banner Section ────────────────────────────────────────────────────────

function BannerVideoField({ slideIdx }: { slideIdx: number }) {
  const { content, update } = useContentStore();
  const slide = content.bannerSlides[slideIdx];
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlInput, setUrlInput] = useState(slide?.videoUrl ?? "");

  useEffect(() => {
    setUrlInput(slide?.videoUrl ?? "");
    setFileName(slide?.videoUrl ? (slide.videoUrl.startsWith("data:") ? "已内嵌视频" : "外链视频") : null);
  }, [slide?.videoUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      setFileName(file.name);
      update((prev) => {
        const slides = [...prev.bannerSlides];
        slides[slideIdx] = { ...slides[slideIdx], videoUrl: dataUrl };
        return { ...prev, bannerSlides: slides };
      });
      setUrlInput(dataUrl);
      setUploadDone(true);
      setTimeout(() => setUploadDone(false), 2000);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLocal = async () => {
    setFileName(null);
    setUrlInput("");
    update((prev) => {
      const slides = [...prev.bannerSlides];
      slides[slideIdx] = { ...slides[slideIdx], videoUrl: "" };
      return { ...prev, bannerSlides: slides };
    });
  };

  const handleApplyUrl = () => {
    update((prev) => {
      const slides = [...prev.bannerSlides];
      slides[slideIdx] = { ...slides[slideIdx], videoUrl: urlInput.trim() };
      return { ...prev, bannerSlides: slides };
    });
    setFileName(urlInput.trim() ? "外链视频" : null);
  };

  const handleClearUrl = () => {
    setUrlInput("");
    setFileName(null);
    update((prev) => {
      const slides = [...prev.bannerSlides];
      slides[slideIdx] = { ...slides[slideIdx], videoUrl: "" };
      return { ...prev, bannerSlides: slides };
    });
  };

  return (
    <div className="bg-[#0a1628] border border-[#1a2840] rounded-xl p-4 space-y-3">
      <p className="text-white text-xs font-semibold flex items-center gap-2">
        <i className="ri-video-line text-[#4fc3f7]" />
        视频背景（优先级高于背景图）
      </p>

      {/* URL input */}
      <div className="flex flex-col gap-1">
        <label className="text-[#8aaec8] text-xs">视频链接（MP4 / WebM）</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/video.mp4"
            className="flex-1 min-w-0 bg-[#070c1a] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
          />
          <button
            onClick={handleApplyUrl}
            className="px-3 py-2 rounded-lg bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] text-xs font-semibold hover:bg-[#4fc3f7]/25 transition-colors cursor-pointer whitespace-nowrap"
          >
            应用
          </button>
          {urlInput && (
            <button onClick={handleClearUrl} className="px-2 py-2 rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] transition-colors cursor-pointer text-xs whitespace-nowrap">
              清除
            </button>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-[1px] bg-[#1e2d4a]" />
        <span className="text-[#3a5a7a] text-xs">或</span>
        <div className="flex-1 h-[1px] bg-[#1e2d4a]" />
      </div>

      {/* Embedded file upload */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] text-xs font-semibold hover:bg-[#4fc3f7]/25 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-upload-2-line" />
          {uploading ? "上传中..." : uploadDone ? "上传成功!" : "上传视频文件"}
        </button>
        {fileName && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <i className="ri-film-line text-cyan-400 text-sm flex-shrink-0" />
            <span className="text-gray-400 text-xs truncate">{fileName}</span>
            <button
              onClick={handleDeleteLocal}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] transition-colors cursor-pointer"
            >
              <i className="ri-delete-bin-line text-xs" />
            </button>
          </div>
        )}
        {!fileName && <span className="text-[#3a5a7a] text-xs">暂无视频文件</span>}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/webm,video/ogg,video/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      <p className="text-[#5a7a9a] text-xs">
        <i className="ri-information-line mr-1 text-[#4fc3f7]" />
        设置视频后，该幻灯片将以视频作为背景（自动播放、静音、循环），背景图作为封面保底。上传文件会直接写入内容数据，保存后访客可见。
      </p>
    </div>
  );
}

function BannerSection() {
  const { content, update, addBannerSlide, removeBannerSlide } = useContentStore();

  const updateSlide = (idx: number, key: keyof BannerSlide, val: string) => {
    update((prev) => {
      const slides = [...prev.bannerSlides];
      slides[idx] = { ...slides[idx], [key]: val };
      return { ...prev, bannerSlides: slides };
    });
  };

  return (
    <div className="space-y-4">
      {content.bannerSlides.map((slide, i) => (
        <div key={i} className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-white text-sm font-semibold flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center rounded bg-[#4fc3f7]/20 text-[#4fc3f7] text-xs font-bold">
                {i + 1}
              </span>
              幻灯片 {i + 1}
            </p>
            <DeleteButton onClick={() => removeBannerSlide(i)} disabled={content.bannerSlides.length <= 1} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldInput label="中文标题" value={slide.zhTitle} onChange={(v) => updateSlide(i, "zhTitle", v)} />
            <FieldInput label="英文标题" value={slide.enTitle} onChange={(v) => updateSlide(i, "enTitle", v)} />
            <FieldInput label="中文副标题" value={slide.zhSub} onChange={(v) => updateSlide(i, "zhSub", v)} />
            <FieldInput label="英文副标题" value={slide.enSub} onChange={(v) => updateSlide(i, "enSub", v)} />
          </div>
          <div className="mt-4 pt-4 border-t border-[#1e2d4a]">
            <ImageField label="背景图（无视频时显示 / 视频加载中封面）" value={slide.image} onChange={(v) => updateSlide(i, "image", v)} aspectRatio="16/9" storageKey={`bannerSlides.${i}.image`} />
          </div>
          <div className="mt-4">
            <BannerVideoField slideIdx={i} />
          </div>
        </div>
      ))}
      <AddButton label="添加幻灯片" onClick={addBannerSlide} />
    </div>
  );
}

// ─── About Section ─────────────────────────────────────────────────────────

function AboutSection() {
  const { content, update, addStat, removeStat } = useContentStore();

  const updateStat = (idx: number, key: keyof AboutStat, val: string) => {
    update((prev) => {
      const stats = [...prev.aboutStats];
      stats[idx] = { ...stats[idx], [key]: val };
      return { ...prev, aboutStats: stats };
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-bar-chart-line text-[#4fc3f7]"></i>
          数字指标
          <span className="ml-auto text-[#5a7a9a] text-xs font-normal">共 {content.aboutStats.length} 项</span>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          {content.aboutStats.map((stat, i) => (
            <div key={i} className="bg-[#0a1628] rounded-lg p-4 space-y-3 border border-[#1a2840]">
              <div className="flex items-center justify-between">
                <p className="text-[#5a7a9a] text-xs">指标 {i + 1}</p>
                <DeleteButton onClick={() => removeStat(i)} disabled={content.aboutStats.length <= 1} />
              </div>
              <FieldInput label="数值（如 15+）" value={stat.num} onChange={(v) => updateStat(i, "num", v)} />
              <FieldInput label="中文说明" value={stat.zhLabel} onChange={(v) => updateStat(i, "zhLabel", v)} />
              <FieldInput label="英文说明" value={stat.enLabel} onChange={(v) => updateStat(i, "enLabel", v)} />
            </div>
          ))}
        </div>
        <AddButton label="添加指标" onClick={addStat} />
      </div>

      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-file-text-line text-[#4fc3f7]"></i>
          公司简介文案
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldInput label="中文段落 1" value={content.aboutZhDesc1} onChange={(v) => update((p) => ({ ...p, aboutZhDesc1: v }))} multiline />
          <FieldInput label="英文段落 1" value={content.aboutEnDesc1} onChange={(v) => update((p) => ({ ...p, aboutEnDesc1: v }))} multiline />
          <FieldInput label="中文段落 2" value={content.aboutZhDesc2} onChange={(v) => update((p) => ({ ...p, aboutZhDesc2: v }))} multiline />
          <FieldInput label="英文段落 2" value={content.aboutEnDesc2} onChange={(v) => update((p) => ({ ...p, aboutEnDesc2: v }))} multiline />
        </div>
      </div>

      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-image-line text-[#4fc3f7]"></i>
          公司展示图片
        </p>
        <div className="max-w-sm">
          <ImageField label="公司图片" value={content.aboutImage} onChange={(v) => update((p) => ({ ...p, aboutImage: v }))} aspectRatio="4/3" storageKey="aboutImage" />
        </div>
      </div>
    </div>
  );
}

// ─── Products Section ──────────────────────────────────────────────────────

const PRODUCT_COLORS = [
  { accent: "text-cyan-400", border: "border-cyan-500/30", bg: "bg-cyan-500/10" },
  { accent: "text-orange-400", border: "border-orange-500/30", bg: "bg-orange-500/10" },
  { accent: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10" },
  { accent: "text-violet-400", border: "border-violet-500/30", bg: "bg-violet-500/10" },
  { accent: "text-rose-400", border: "border-rose-500/30", bg: "bg-rose-500/10" },
  { accent: "text-amber-400", border: "border-amber-500/30", bg: "bg-amber-500/10" },
];

function ProductsSection() {
  const { content, update, addProduct, removeProduct } = useContentStore();

  const updateProduct = (idx: number, key: keyof ProductItem, val: string) => {
    update((prev) => {
      const products = [...prev.products];
      products[idx] = { ...products[idx], [key]: val };
      return { ...prev, products };
    });
  };

  return (
    <div className="space-y-4">
      {content.products.map((prod, i) => {
        const color = PRODUCT_COLORS[i % PRODUCT_COLORS.length];
        return (
          <div key={i} className={`bg-[#0d1529] border ${color.border} rounded-xl p-5`}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-white text-sm font-semibold flex items-center gap-2">
                <span className={`w-5 h-5 flex items-center justify-center rounded ${color.bg} ${color.accent} text-xs font-bold`}>
                  {i + 1}
                </span>
                {prod.zhTitle || `产品 ${i + 1}`}
              </p>
              <DeleteButton onClick={() => removeProduct(i)} disabled={content.products.length <= 1} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <FieldInput label="中文标题" value={prod.zhTitle} onChange={(v) => updateProduct(i, "zhTitle", v)} />
              <FieldInput label="英文标题" value={prod.enTitle} onChange={(v) => updateProduct(i, "enTitle", v)} />
              <FieldInput label="中文描述" value={prod.zhDesc} onChange={(v) => updateProduct(i, "zhDesc", v)} multiline />
              <FieldInput label="英文描述" value={prod.enDesc} onChange={(v) => updateProduct(i, "enDesc", v)} multiline />
            </div>
            <div className="pt-4 border-t border-[#1e2d4a]">
              <ImageField label="产品卡片图" value={prod.image} onChange={(v) => updateProduct(i, "image", v)} aspectRatio="4/3" storageKey={`products.${i}.image`} />
            </div>
          </div>
        );
      })}
      <AddButton label="添加产品/服务卡片" onClick={addProduct} />
    </div>
  );
}

// ─── Contact Section ───────────────────────────────────────────────────────

const CONTACT_ICONS = [
  "ri-map-pin-2-line",
  "ri-phone-line",
  "ri-mail-line",
  "ri-time-line",
  "ri-wechat-line",
  "ri-whatsapp-line",
  "ri-links-line",
  "ri-global-line",
  "ri-customer-service-2-line",
  "ri-information-line",
];

function IconPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[#8aaec8] text-xs">图标</label>
      <div className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-2 bg-[#0a1628] border border-[#1e2d4a] rounded-lg text-white text-xs hover:border-[#4fc3f7] transition-colors cursor-pointer w-full"
        >
          <i className={`${value} text-[#4fc3f7]`} />
          <span className="text-[#8aaec8]">{value}</span>
          <i className="ri-arrow-down-s-line ml-auto text-[#5a7a9a]" />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-3 w-56 shadow-xl">
            <div className="grid grid-cols-5 gap-2">
              {CONTACT_ICONS.map((icon) => (
                <button
                  key={icon}
                  onClick={() => { onChange(icon); setOpen(false); }}
                  title={icon}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                    value === icon
                      ? "bg-[#4fc3f7]/20 text-[#4fc3f7] border border-[#4fc3f7]/40"
                      : "text-[#5a7a9a] hover:text-white hover:bg-white/5"
                  }`}
                >
                  <i className={`${icon} text-base`} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactSection() {
  const { content, updateContactInfo, updateContactField, addContactInfo, removeContactInfo, updateAIChat } =
    useContentStore();
  const { contact, aiChat } = content;

  return (
    <div className="space-y-4">
      {/* Section titles */}
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-heading text-[#4fc3f7]"></i>
          栏目标题与说明
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldInput
            label="中文主标题"
            value={contact.zhTitle}
            onChange={(v) => updateContactField("zhTitle", v)}
          />
          <FieldInput
            label="英文主标题"
            value={contact.enTitle}
            onChange={(v) => updateContactField("enTitle", v)}
          />
          <FieldInput
            label="中文副标题"
            value={contact.zhSubtitle}
            onChange={(v) => updateContactField("zhSubtitle", v)}
            multiline
          />
          <FieldInput
            label="英文副标题"
            value={contact.enSubtitle}
            onChange={(v) => updateContactField("enSubtitle", v)}
            multiline
          />
          <FieldInput
            label="表单标题（中文）"
            value={contact.zhFormTitle}
            onChange={(v) => updateContactField("zhFormTitle", v)}
          />
          <FieldInput
            label="表单标题（英文）"
            value={contact.enFormTitle}
            onChange={(v) => updateContactField("enFormTitle", v)}
          />
        </div>
      </div>

      {/* Contact info items */}
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-contacts-line text-[#4fc3f7]"></i>
          联系方式条目
          <span className="ml-auto text-[#5a7a9a] text-xs font-normal">共 {contact.infoItems.length} 条</span>
        </p>
        <div className="space-y-4 mb-4">
          {contact.infoItems.map((item, i) => (
            <div key={i} className="bg-[#0a1628] border border-[#1a2840] rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[#5a7a9a] text-xs flex items-center gap-1.5">
                  <i className={`${item.icon} text-emerald-400`} />
                  条目 {i + 1}
                </span>
                <DeleteButton
                  onClick={() => removeContactInfo(i)}
                  disabled={contact.infoItems.length <= 1}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <IconPicker
                    value={item.icon}
                    onChange={(v) => updateContactInfo(i, "icon", v)}
                  />
                </div>
                <FieldInput
                  label="中文标签"
                  value={item.zhLabel}
                  onChange={(v) => updateContactInfo(i, "zhLabel", v)}
                  placeholder="如：公司地址"
                />
                <FieldInput
                  label="英文标签"
                  value={item.enLabel}
                  onChange={(v) => updateContactInfo(i, "enLabel", v)}
                  placeholder="e.g. Address"
                />
                <FieldInput
                  label="中文内容"
                  value={item.zhValue}
                  onChange={(v) => updateContactInfo(i, "zhValue", v)}
                  placeholder="如：北京市海淀区..."
                />
                <FieldInput
                  label="英文内容"
                  value={item.enValue}
                  onChange={(v) => updateContactInfo(i, "enValue", v)}
                  placeholder="e.g. Floor 8, Bldg A..."
                />
              </div>
            </div>
          ))}
        </div>
        <AddButton label="添加联系方式" onClick={addContactInfo} />
      </div>

      {/* Map embed URL */}
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-1 flex items-center gap-2">
          <i className="ri-map-2-line text-[#4fc3f7]"></i>
          地图嵌入 URL
        </p>
        <p className="text-[#5a7a9a] text-xs mb-4">
          在 Google Maps 中找到地址 → 点「分享」→「嵌入地图」→ 复制 src 链接粘贴到下方
        </p>
        <FieldInput
          label="Google Maps Embed URL"
          value={contact.mapEmbedUrl}
          onChange={(v) => updateContactField("mapEmbedUrl", v)}
          placeholder="https://www.google.com/maps/embed?pb=..."
        />
      </div>

      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-robot-2-line text-[#4fc3f7]"></i>
          AI 智能客服
          <button
            type="button"
            onClick={() => updateAIChat("enabled", !aiChat.enabled)}
            className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${aiChat.enabled ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-[#1e2d4a] text-[#5a7a9a] border border-[#1e2d4a]"}`}
          >
            <i className={aiChat.enabled ? "ri-eye-line" : "ri-eye-off-line"} />
            {aiChat.enabled ? "显示中" : "已隐藏"}
          </button>
        </p>
        <div className="space-y-3">
          <FieldInput
            label="AI 智能体接口地址"
            value={aiChat.endpoint}
            onChange={(v) => updateAIChat("endpoint", v)}
            placeholder="如：https://your-domain.com/api/agent"
          />
          <p className="text-[#5a7a9a] text-xs leading-5">
            留空时，前台继续使用当前内置问答；填写后，前台会向该地址发送 `message`、`lang`、`history` 三个字段并优先显示接口返回内容。
          </p>
        </div>
      </div>
    </div>
  );
}

function TechDocHtmlEditor({
  label,
  value,
  onChange,
  onExternalInsert,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onExternalInsert?: (html: string) => void;
}) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<HTMLImageElement | HTMLVideoElement | HTMLIFrameElement | null>(null);
  const mediaSelector = "img, video, iframe";
  const dragStateRef = useRef<{ block: HTMLElement; startX: number; startY: number; startLeft: number; startTop: number } | null>(null);
  const resizeStateRef = useRef<{ block: HTMLElement; startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);

  const ensureMediaHandles = (block: HTMLElement) => {
    let dragHandle = block.querySelector("[data-media-drag-handle='1']") as HTMLButtonElement | null;
    if (!dragHandle) {
      dragHandle = document.createElement("button");
      dragHandle.type = "button";
      dragHandle.dataset.mediaDragHandle = "1";
      dragHandle.textContent = "拖动";
      block.appendChild(dragHandle);
    }

    let resizeHandle = block.querySelector("[data-media-resize-handle='1']") as HTMLButtonElement | null;
    if (!resizeHandle) {
      resizeHandle = document.createElement("button");
      resizeHandle.type = "button";
      resizeHandle.dataset.mediaResizeHandle = "1";
      resizeHandle.setAttribute("aria-label", "调整大小");
      resizeHandle.textContent = "↘";
      block.appendChild(resizeHandle);
    }

    dragHandle.contentEditable = "false";
    resizeHandle.contentEditable = "false";
  };

  const getMediaBlock = (node: HTMLElement | null): HTMLElement | null => {
    if (!node) return null;
    if (node.dataset.mediaBlock === "1") return node;
    return node.closest("[data-media-block='1']") as HTMLElement | null;
  };

  const normalizeMediaBlock = (block: HTMLElement) => {
    ensureMediaHandles(block);
    block.style.position = "absolute";
    block.style.margin = "0";
    block.style.maxWidth = "none";
    block.style.minWidth = "120px";
    block.style.minHeight = "90px";
    block.style.overflow = "visible";
    block.style.cursor = "default";
    if (!block.style.left) block.style.left = "16px";
    if (!block.style.top) block.style.top = "16px";
    if (!block.style.width) block.style.width = "320px";
    if (!block.style.height) block.style.height = "180px";
    const media = getPrimaryMedia(block);
    if (media) {
      media.style.width = "100%";
      media.style.height = "100%";
      media.style.display = "block";
      if (media.tagName !== "IFRAME") media.style.objectFit = "contain";
    }
  };

  const wrapMediaIfNeeded = (media: HTMLImageElement | HTMLVideoElement | HTMLIFrameElement): HTMLElement => {
    const existing = getMediaBlock(media);
    if (existing) {
      normalizeMediaBlock(existing);
      return existing;
    }
    const figure = document.createElement("figure");
    figure.dataset.mediaBlock = "1";
    figure.contentEditable = "false";
    media.parentNode?.insertBefore(figure, media);
    figure.appendChild(media);
    normalizeMediaBlock(figure);
    return figure;
  };

  const getPrimaryMedia = (block: HTMLElement | null): HTMLImageElement | HTMLVideoElement | HTMLIFrameElement | null => {
    if (!block) return null;
    return block.querySelector(mediaSelector) as HTMLImageElement | HTMLVideoElement | HTMLIFrameElement | null;
  };

  const bindVideoEvents = (root: HTMLElement) => {
    root.querySelectorAll("video").forEach((video) => {
      video.controls = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      video.setAttribute("x5-playsinline", "true");
      video.preload = "metadata";
      video.muted = false;
    });
  };

  const refreshEditorContent = () => {
    const root = editorRef.current;
    if (!root) return;
    bindVideoEvents(root);
    const snapshot = root.cloneNode(true) as HTMLDivElement;
    snapshot.querySelectorAll("[data-media-drag-handle='1'], [data-media-resize-handle='1']").forEach((node) => node.remove());
    onChange(snapshot.innerHTML ?? "");
  };

  const placeCaretAfterBlock = (block: HTMLElement | null) => {
    const root = editorRef.current;
    if (!root || !block || !block.parentNode) return;
    const spacer = document.createTextNode("\u00a0");
    if (block.nextSibling) {
      block.parentNode.insertBefore(spacer, block.nextSibling);
    } else {
      block.parentNode.appendChild(spacer);
    }
    const selection = window.getSelection();
    const range = document.createRange();
    range.setStart(spacer, spacer.textContent?.length ?? 1);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    root.focus();
  };

  const insertMediaFigure = (innerHtml: string): HTMLElement | null => {
    const count = editorRef.current?.querySelectorAll("[data-media-block='1']").length ?? 0;
    const top = 16 + count * 28;
    const marker = `media-block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    document.execCommand(
      "insertHTML",
      false,
      `<figure data-media-block="1" data-media-marker="${marker}" contenteditable="false" style="position:absolute;left:16px;top:${top}px;width:320px;height:180px;margin:0;max-width:none;min-width:120px;min-height:90px;overflow:visible;cursor:move;">${innerHtml}</figure>`,
    );
    const block = editorRef.current?.querySelector(`[data-media-marker='${marker}']`) as HTMLElement | null;
    if (block) block.removeAttribute("data-media-marker");
    return block ?? null;
  };

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value;
    }
    el.querySelectorAll("[data-media-block='1']").forEach((node) => normalizeMediaBlock(node as HTMLElement));
    bindVideoEvents(el);
  }, [value]);

  const focusEditor = () => editorRef.current?.focus();

  const handleInsertImageUrl = () => {
    const url = window.prompt("请输入图片 URL（可粘贴 data:image/...）");
    if (!url) return;
    focusEditor();
    const block = insertMediaFigure(`<img src="${url}" style="width:100%;height:100%;display:block;object-fit:contain;" />`);
    placeCaretAfterBlock(block);
    refreshEditorContent();
  };

  const handleInsertVideoUrl = () => {
    const url = window.prompt("请输入可直接播放的视频 URL（如 mp4/webm/ogg）");
    if (!url) return;
    focusEditor();
    const block = insertMediaFigure(buildVideoPreviewHtml(url));
    placeCaretAfterBlock(block);
    refreshEditorContent();
  };

  const handleUploadAndInsert = async (type: "image" | "video", file: File) => {
    focusEditor();
    let block: HTMLElement | null = null;
    if (type === "image") {
      const dataUrl = await fileToDataUrl(file);
      block = insertMediaFigure(`<img src="${dataUrl}" style="width:100%;height:100%;display:block;object-fit:contain;" />`);
    } else {
      const uploadedUrl = await uploadMediaFile(file, "video");
      const videoHtml = buildVideoFigureHtml(uploadedUrl);
      block = insertMediaFigure(videoHtml);
      onExternalInsert?.(videoHtml);
    }
    placeCaretAfterBlock(block);
    refreshEditorContent();
  };

  const removeSelectedMedia = () => {
    if (!selectedMedia) return;
    const block = getMediaBlock(selectedMedia);
    if (!block) return;
    block.remove();
    setSelectedMedia(null);
    refreshEditorContent();
  };

  const updateSelectedMedia = (media: HTMLImageElement | HTMLVideoElement | HTMLIFrameElement | null) => {
    setSelectedMedia(media);
  };

  const beginInteraction = (block: HTMLElement, clientX: number, clientY: number) => {
    normalizeMediaBlock(block);
    const rect = block.getBoundingClientRect();
    const dragHandle = block.querySelector("[data-media-drag-handle='1']") as HTMLElement | null;
    const resizeHandle = block.querySelector("[data-media-resize-handle='1']") as HTMLElement | null;
    const dragRect = dragHandle?.getBoundingClientRect();
    const resizeRect = resizeHandle?.getBoundingClientRect();
    const onDragHandle = !!dragRect && clientX >= dragRect.left && clientX <= dragRect.right && clientY >= dragRect.top && clientY <= dragRect.bottom;
    const onResizeHandle = !!resizeRect && clientX >= resizeRect.left && clientX <= resizeRect.right && clientY >= resizeRect.top && clientY <= resizeRect.bottom;
    if (!onDragHandle && !onResizeHandle) return;
    const offsetX = clientX - rect.left;
    const offsetY = clientY - rect.top;
    const resizing = rect.width - offsetX < 20 || rect.height - offsetY < 20;
    if (onResizeHandle || resizing) {
      resizeStateRef.current = { block, startX: clientX, startY: clientY, startWidth: rect.width, startHeight: rect.height };
      dragStateRef.current = null;
      return;
    }
    dragStateRef.current = {
      block,
      startX: clientX,
      startY: clientY,
      startLeft: parseFloat(block.style.left || "0"),
      startTop: parseFloat(block.style.top || "0"),
    };
    resizeStateRef.current = null;
  };

  const resizeSelectedMediaByWheel = (media: HTMLImageElement | HTMLVideoElement | HTMLIFrameElement, deltaY: number) => {
    const block = getMediaBlock(media);
    if (!block) return;
    normalizeMediaBlock(block);
    const width = parseFloat(block.style.width || "320");
    const height = parseFloat(block.style.height || "180");
    const ratio = width / Math.max(height, 1);
    const nextWidth = Math.max(120, width + (deltaY < 0 ? 24 : -24));
    const nextHeight = Math.max(90, nextWidth / Math.max(ratio, 0.2));
    block.style.width = `${nextWidth}px`;
    block.style.height = `${nextHeight}px`;
    media.style.width = "100%";
    media.style.height = "100%";
    refreshEditorContent();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const root = editorRef.current;
      if (!root) return;
      if (resizeStateRef.current) {
        const state = resizeStateRef.current;
        const nextWidth = Math.max(120, state.startWidth + (e.clientX - state.startX));
        const nextHeight = Math.max(90, state.startHeight + (e.clientY - state.startY));
        state.block.style.width = `${nextWidth}px`;
        state.block.style.height = `${nextHeight}px`;
        const media = getPrimaryMedia(state.block);
        if (media) {
          media.style.width = "100%";
          media.style.height = "100%";
        }
        return;
      }
      if (dragStateRef.current) {
        const state = dragStateRef.current;
        const maxLeft = Math.max(0, root.clientWidth - 60);
        const maxTop = Math.max(0, root.scrollHeight + 400);
        const nextLeft = Math.max(0, Math.min(maxLeft, state.startLeft + (e.clientX - state.startX)));
        const nextTop = Math.max(0, Math.min(maxTop, state.startTop + (e.clientY - state.startY)));
        state.block.style.left = `${nextLeft}px`;
        state.block.style.top = `${nextTop}px`;
      }
    };

    const handleMouseUp = () => {
      if (!dragStateRef.current && !resizeStateRef.current) return;
      dragStateRef.current = null;
      resizeStateRef.current = null;
      onChange(editorRef.current?.innerHTML ?? "");
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [onChange]);

  return (
    <div className="space-y-2">
      <label className="text-[#8aaec8] text-xs">{label}</label>
      <div className="rounded-xl border border-[#1e2d4a] bg-[#0a1628] p-3 space-y-3">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleInsertImageUrl} className="px-2 py-1 text-xs rounded bg-white/5 border border-white/10 text-white">插入图片URL</button>
          <button type="button" onClick={handleInsertVideoUrl} className="px-2 py-1 text-xs rounded bg-white/5 border border-white/10 text-white">插入视频URL</button>
          <label className="px-2 py-1 text-xs rounded bg-white/5 border border-white/10 text-white cursor-pointer">
            上传图片
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUploadAndInsert("image", file);
                e.currentTarget.value = "";
              }}
            />
          </label>
          <label className="px-2 py-1 text-xs rounded bg-white/5 border border-white/10 text-white cursor-pointer">
            上传视频
            <input
              type="file"
              accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-m4v"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUploadAndInsert("video", file);
                e.currentTarget.value = "";
              }}
            />
          </label>
        </div>

        {selectedMedia && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#2b466d] bg-[#071120] px-3 py-2">
            <span className="text-[#8aaec8] text-xs">已选中媒体</span>
            <button type="button" onClick={removeSelectedMedia} className="px-2 py-1 text-xs rounded bg-red-500/10 border border-red-500/30 text-red-300">删除媒体</button>
            <span className="text-[#5a7a9a] text-xs">提示：用左上角“拖动”按钮移动媒体，用右下角手柄调大小；视频画面本身可直接点击播放；滚轮可快速缩放。</span>
          </div>
        )}

        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={() => onChange(editorRef.current?.innerHTML ?? "")}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            const target = e.target as HTMLElement;
            const block = getMediaBlock(target);
            if (!block) return;
            const media = target.matches(mediaSelector)
              ? (target as HTMLImageElement | HTMLVideoElement | HTMLIFrameElement)
              : getPrimaryMedia(block);
            updateSelectedMedia(media ?? null);
            beginInteraction(block, e.clientX, e.clientY);
            if (target.closest("[data-media-drag-handle='1']") || target.closest("[data-media-resize-handle='1']")) {
              e.preventDefault();
            }
          }}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            const block = getMediaBlock(target);
            const media = target.matches(mediaSelector)
              ? (target as HTMLImageElement | HTMLVideoElement | HTMLIFrameElement)
              : getPrimaryMedia(block);
            updateSelectedMedia(media ?? null);
            if (media instanceof HTMLVideoElement) media.controls = true;
          }}
          onKeyDown={(e) => {
            if ((e.key === "Delete" || e.key === "Backspace") && selectedMedia) {
              e.preventDefault();
              removeSelectedMedia();
            }
          }}
          onWheel={(e) => {
            const target = e.target as HTMLElement;
            const media = target.matches(mediaSelector)
              ? (target as HTMLImageElement | HTMLVideoElement | HTMLIFrameElement)
              : getPrimaryMedia(getMediaBlock(target));
            if (!media) return;
            wrapMediaIfNeeded(media);
            updateSelectedMedia(media);
            e.preventDefault();
            resizeSelectedMediaByWheel(media, e.deltaY);
          }}
          className="relative min-h-[520px] rounded-lg border border-gray-300 bg-white p-4 text-black focus:outline-none [&_[data-media-block='1']]:outline-none [&_[data-media-block='1']]:shadow-none [&_[data-media-block='1']]:border-0 [&_[data-media-block='1']]:select-none [&_[data-media-block='1']_[data-media-drag-handle='1']]:absolute [&_[data-media-block='1']_[data-media-drag-handle='1']]:left-2 [&_[data-media-block='1']_[data-media-drag-handle='1']]:top-2 [&_[data-media-block='1']_[data-media-drag-handle='1']]:z-20 [&_[data-media-block='1']_[data-media-drag-handle='1']]:rounded [&_[data-media-block='1']_[data-media-drag-handle='1']]:border [&_[data-media-block='1']_[data-media-drag-handle='1']]:border-white/20 [&_[data-media-block='1']_[data-media-drag-handle='1']]:bg-black/65 [&_[data-media-block='1']_[data-media-drag-handle='1']]:px-2 [&_[data-media-block='1']_[data-media-drag-handle='1']]:py-1 [&_[data-media-block='1']_[data-media-drag-handle='1']]:text-[10px] [&_[data-media-block='1']_[data-media-drag-handle='1']]:text-white [&_[data-media-block='1']_[data-media-drag-handle='1']]:cursor-move [&_[data-media-block='1']_[data-media-resize-handle='1']]:absolute [&_[data-media-block='1']_[data-media-resize-handle='1']]:bottom-2 [&_[data-media-block='1']_[data-media-resize-handle='1']]:right-2 [&_[data-media-block='1']_[data-media-resize-handle='1']]:z-20 [&_[data-media-block='1']_[data-media-resize-handle='1']]:flex [&_[data-media-block='1']_[data-media-resize-handle='1']]:h-6 [&_[data-media-block='1']_[data-media-resize-handle='1']]:w-6 [&_[data-media-block='1']_[data-media-resize-handle='1']]:items-center [&_[data-media-block='1']_[data-media-resize-handle='1']]:justify-center [&_[data-media-block='1']_[data-media-resize-handle='1']]:rounded-full [&_[data-media-block='1']_[data-media-resize-handle='1']]:border [&_[data-media-block='1']_[data-media-resize-handle='1']]:border-white/20 [&_[data-media-block='1']_[data-media-resize-handle='1']]:bg-black/65 [&_[data-media-block='1']_[data-media-resize-handle='1']]:text-xs [&_[data-media-block='1']_[data-media-resize-handle='1']]:text-white [&_[data-media-block='1']_[data-media-resize-handle='1']]:cursor-se-resize [&_[data-media-block='1']_img]:block [&_[data-media-block='1']_img]:w-full [&_[data-media-block='1']_img]:h-full [&_[data-media-block='1']_img]:object-contain [&_[data-media-block='1']_video]:block [&_[data-media-block='1']_video]:w-full [&_[data-media-block='1']_video]:h-full [&_[data-media-block='1']_video]:max-w-none [&_[data-media-block='1']_video]:bg-black [&_[data-media-block='1']_video]:object-contain [&_[data-media-block='1']_iframe]:block [&_[data-media-block='1']_iframe]:w-full [&_[data-media-block='1']_iframe]:h-full [&_[data-media-block='1']_iframe]:max-w-none [&_[data-media-block='1']_iframe]:bg-black"
        />
      </div>
    </div>
  );
}

function NewsArticlesSection() {
  const { content, addNewsArticle, removeNewsArticle, updateNewsArticle } = useContentStore();
  const articles = [...content.newsArticles].sort((a, b) => a.order - b.order);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const insertVideoIntoEnglishContent = (sourceIndex: number, html: string) => {
    const current = content.newsArticles[sourceIndex];
    if (!current) return;
    const base = current.enContentHtml ?? "";
    const spacer = base.trim() ? "&nbsp;" : "";
    const nextHtml = `${base}${spacer}${html}`;
    updateNewsArticle(sourceIndex, "enContentHtml", nextHtml);
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => ({ ...current, [id]: !current[id] }));
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-newspaper-line text-[#4fc3f7]"></i>
          新闻发布
          <span className="ml-auto text-[#5a7a9a] text-xs">共 {articles.length} 篇</span>
        </p>
        <div className="mb-4 rounded-xl border border-[#27466b] bg-[#081321] px-4 py-3 text-xs text-[#9fc0dd] leading-6">
          新闻发布正文已改为自由画布式媒体编辑：图片、视频和嵌入播放器都可在编辑框内自由拖动位置、自由调整宽高，且不再显示高亮外框与 Word 样式工具栏。
        </div>
        <div className="space-y-4 mb-4">
          {articles.map((item) => {
            const sourceIndex = content.newsArticles.findIndex((article) => article.id === item.id);
            const expanded = expandedIds[item.id] ?? false;
            return (
              <div key={item.id} className={`rounded-xl border p-4 transition-colors ${expanded ? "border-[#2b466d] bg-[#0a1628] space-y-3" : "border-[#16233a] bg-[#09111f]"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      <span className="rounded-full border border-[#2b466d] bg-[#081321] px-2 py-0.5 text-[#8aaec8]">新闻 #{item.order}</span>
                      <span className="text-[#5a7a9a]">{item.id}</span>
                      <span className="text-[#5a7a9a]">{item.publishDate}</span>
                    </div>
                    <div className="mt-2 truncate text-white text-sm font-medium">{item.zhTitle || item.enTitle || "未命名新闻"}</div>
                    {!expanded && (
                      <div className="mt-1 truncate text-[#6f8ca8] text-xs">{item.zhSummary || item.enSummary || "点击编辑展开完整内容"}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => toggleExpanded(item.id)} className={`px-2.5 py-1 text-xs rounded border transition-colors ${expanded ? "bg-[#17304d] border-[#2b466d] text-[#9fd8ff]" : "bg-white/5 border-white/10 text-white hover:border-[#2b466d]"}`}>
                      {expanded ? "收起" : "编辑"}
                    </button>
                    <DeleteButton onClick={() => removeNewsArticle(sourceIndex)} disabled={articles.length <= 1} />
                  </div>
                </div>
                {expanded && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FieldInput label="排序序号" value={String(item.order)} onChange={(v) => updateNewsArticle(sourceIndex, "order", Number(v) || 1)} />
                      <FieldInput label="新闻 ID" value={item.id} onChange={(v) => updateNewsArticle(sourceIndex, "id", v)} />
                      <FieldInput label="发布日期" value={item.publishDate} onChange={(v) => updateNewsArticle(sourceIndex, "publishDate", v)} placeholder="2026-05-05" />
                      <FieldInput label="中文标题" value={item.zhTitle} onChange={(v) => updateNewsArticle(sourceIndex, "zhTitle", v)} />
                      <FieldInput label="英文标题" value={item.enTitle} onChange={(v) => updateNewsArticle(sourceIndex, "enTitle", v)} />
                      <FieldInput label="中文摘要" value={item.zhSummary} onChange={(v) => updateNewsArticle(sourceIndex, "zhSummary", v)} multiline />
                      <FieldInput label="英文摘要" value={item.enSummary} onChange={(v) => updateNewsArticle(sourceIndex, "enSummary", v)} multiline />
                    </div>
                    <TechDocHtmlEditor
                      label="正文（中文）"
                      value={item.zhContentHtml}
                      onChange={(v) => updateNewsArticle(sourceIndex, "zhContentHtml", v)}
                      onExternalInsert={(html) => insertVideoIntoEnglishContent(sourceIndex, html)}
                    />
                    <TechDocHtmlEditor label="正文（英文）" value={item.enContentHtml} onChange={(v) => updateNewsArticle(sourceIndex, "enContentHtml", v)} />
                  </>
                )}
              </div>
            );
          })}
        </div>
        <AddButton label="新增新闻" onClick={addNewsArticle} />
      </div>
    </div>
  );
}

// ─── Footer Section (Social + Legal + Friend Links) ───────────────────────

const SOCIAL_ICONS = [
  "ri-wechat-line", "ri-weibo-line", "ri-linkedin-line", "ri-mail-line",
  "ri-twitter-x-line", "ri-facebook-line", "ri-instagram-line", "ri-youtube-line",
  "ri-github-line", "ri-telegram-line", "ri-whatsapp-line", "ri-tiktok-line",
];

function SocialIconPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[#8aaec8] text-xs">图标</label>
      <div className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-2 bg-[#0a1628] border border-[#1e2d4a] rounded-lg text-white text-xs hover:border-[#4fc3f7] transition-colors cursor-pointer w-full"
        >
          <i className={`${value} text-[#4fc3f7] text-base`} />
          <span className="text-[#8aaec8]">{value}</span>
          <i className="ri-arrow-down-s-line ml-auto text-[#5a7a9a]" />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-3 shadow-xl">
            <div className="grid grid-cols-6 gap-2">
              {SOCIAL_ICONS.map((icon) => (
                <button
                  key={icon}
                  onClick={() => { onChange(icon); setOpen(false); }}
                  title={icon}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${value === icon ? "bg-[#4fc3f7]/20 text-[#4fc3f7] border border-[#4fc3f7]/40" : "text-[#5a7a9a] hover:text-white hover:bg-white/5"}`}
                >
                  <i className={`${icon} text-base`} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type UploadStatus = "idle" | "uploading" | "success" | "error";

function BgMusicSection() {
  const { content, updateBgMusic } = useContentStore();
  const { bgMusic } = content;
  const [urlInput, setUrlInput] = useState(bgMusic.url ?? "");
  const [applied, setApplied] = useState(false);
  // Cloud upload state
  const [cloudUploadStatus, setCloudUploadStatus] = useState<UploadStatus>("idle");
  const [cloudUploadProgress, setCloudUploadProgress] = useState(0);
  const [cloudFileName, setCloudFileName] = useState<string | null>(null);
  const [cloudFileSize, setCloudFileSize] = useState<number>(0);
  const [cloudError, setCloudError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEmbeddedAudio = bgMusic.url?.startsWith("data:") ?? false;

  const handleApply = () => {
    updateBgMusic("url", urlInput.trim());
    setApplied(true);
    setTimeout(() => setApplied(false), 2000);
  };

  const handleClear = () => {
    setUrlInput("");
    updateBgMusic("url", "");
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const handleCloudUpload = async (file: File) => {
    const validTypes = ["audio/mpeg", "audio/ogg", "audio/wav", "audio/mp4", "audio/aac", "audio/flac", "audio/x-m4a", "audio/webm"];
    const isAudio = validTypes.includes(file.type) || /\.(mp3|ogg|wav|m4a|aac|flac|webm)$/i.test(file.name);
    if (!isAudio) {
      setCloudError("不支持的文件格式，请上传 MP3 / OGG / WAV / M4A / AAC / FLAC");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setCloudError("文件大小不能超过 8MB，建议使用压缩后的 MP3 文件");
      return;
    }

    setCloudUploadStatus("uploading");
    setCloudError(null);
    setCloudFileName(file.name);
    setCloudFileSize(file.size);
    setCloudUploadProgress(0);

    // Simulate progress while uploading
    const progressInterval = setInterval(() => {
      setCloudUploadProgress((p) => Math.min(p + Math.random() * 15, 85));
    }, 300);

    try {
      const dataUrl = await fileToDataUrl(file);
      clearInterval(progressInterval);
      setCloudUploadProgress(100);
      setCloudUploadStatus("success");

      setUrlInput(dataUrl);
      updateBgMusic("url", dataUrl);
      window.dispatchEvent(new CustomEvent("bg-audio-updated"));
    } catch (err: unknown) {
      clearInterval(progressInterval);
      setCloudUploadStatus("error");
      setCloudError(err instanceof Error ? err.message : "上传失败，请重试");
    }
  };

  const handleDeleteCloud = async () => {
    // Just clear the URL; optionally could call DELETE endpoint
    updateBgMusic("url", "");
    setUrlInput("");
    setCloudUploadStatus("idle");
    setCloudFileName(null);
    window.dispatchEvent(new CustomEvent("bg-audio-updated"));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleCloudUpload(file);
  };

  return (
    <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
      <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
        <i className="ri-music-2-line text-[#4fc3f7]"></i>
        背景音乐
        <button
          onClick={() => updateBgMusic("enabled", !bgMusic.enabled)}
          className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${bgMusic.enabled ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-[#1e2d4a] text-[#5a7a9a] border border-[#1e2d4a]"}`}
        >
          <i className={bgMusic.enabled ? "ri-eye-line" : "ri-eye-off-line"} />
          {bgMusic.enabled ? "显示中" : "已隐藏"}
        </button>
      </p>
      <div className="space-y-4">
        <FieldInput
          label="曲目显示名称"
          value={bgMusic.name}
          onChange={(v) => updateBgMusic("name", v)}
          placeholder="如：科技氛围乐"
        />

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <label className="text-[#8aaec8] text-xs">默认播放音量</label>
            <span className="text-[#4fc3f7] text-xs font-mono">{Math.round((bgMusic.volume ?? 0.4) * 100)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={Math.round((bgMusic.volume ?? 0.4) * 100)}
            onChange={(e) => updateBgMusic("volume", Number(e.target.value) / 100)}
            className="w-full accent-cyan-400 cursor-pointer"
          />
          <p className="text-[#5a7a9a] text-[10px]">前台自动播放和手动播放都会从 0 音量淡入到该值。</p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[#8aaec8] text-xs">播放策略</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[
              {
                value: "audible-first",
                title: "优先直接播放",
                desc: "先尝试打开网页即有声播放，失败后退到静音预加载。",
              },
              {
                value: "muted-fallback",
                title: "静音预加载",
                desc: "先静音自动播放，访客首次交互后恢复声音，更稳。",
              },
              {
                value: "manual",
                title: "手动播放",
                desc: "完全不自动播放，只显示播放按钮，由访客手动开启。",
              },
            ].map((item) => {
              const active = (bgMusic.strategy ?? "audible-first") === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => updateBgMusic("strategy", item.value)}
                  className={`text-left rounded-xl border px-3 py-3 transition-colors cursor-pointer ${active ? "bg-[#4fc3f7]/10 border-[#4fc3f7]/40" : "bg-[#070c1a] border-[#1e2d4a] hover:border-[#2b466d]"}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`text-xs font-semibold ${active ? "text-[#4fc3f7]" : "text-white"}`}>{item.title}</span>
                    {active && <i className="ri-check-line text-[#4fc3f7] text-sm" />}
                  </div>
                  <p className="text-[10px] leading-4 text-[#6d8ba8]">{item.desc}</p>
                </button>
              );
            })}
          </div>
          <p className="text-[#5a7a9a] text-[10px]">
            推荐优先选择“静音预加载”获取更稳定的兼容性；如果你想尽量恢复“打开即响”的体验，可选择“优先直接播放”。
          </p>
        </div>

        {/* Cloud upload area */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-[#8aaec8] text-xs">上传音频文件到本地内容</label>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px]">
              <i className="ri-hard-drive-2-line text-[10px]" />
              本地内嵌
            </span>
          </div>

          {/* Drop zone */}
          <div
            className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer ${
              dragOver
                ? "border-[#4fc3f7]/70 bg-[#4fc3f7]/8"
                : cloudUploadStatus === "success" || isEmbeddedAudio
                ? "border-emerald-500/40 bg-emerald-500/5"
                : cloudUploadStatus === "error"
                ? "border-red-500/40 bg-red-500/5"
                : "border-[#1e2d4a] hover:border-[#4fc3f7]/40 bg-[#070c1a]"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => cloudUploadStatus !== "uploading" && fileInputRef.current?.click()}
          >
            {cloudUploadStatus === "uploading" ? (
              <div className="p-5 flex flex-col items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#4fc3f7]/10 border border-[#4fc3f7]/20">
                  <i className="ri-loader-4-line text-[#4fc3f7] text-xl animate-spin" />
                </div>
                <div className="w-full">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[#8aaec8] text-xs truncate max-w-[200px]">{cloudFileName}</span>
                    <span className="text-[#4fc3f7] text-xs font-mono">{Math.round(cloudUploadProgress)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-[#1e2d4a] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#4fc3f7] to-cyan-400 rounded-full transition-all duration-300"
                      style={{ width: `${cloudUploadProgress}%` }}
                    />
                  </div>
                  <p className="text-[#5a7a9a] text-[10px] mt-1.5">正在写入本地内容数据...</p>
                </div>
              </div>
            ) : (cloudUploadStatus === "success" || isEmbeddedAudio) && bgMusic.url ? (
              <div className="p-4 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex-shrink-0">
                  <i className="ri-music-fill text-emerald-400 text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">
                    {cloudFileName || bgMusic.url.split("/").pop()?.replace(/^bg_music_\d+_/, "") || "音频文件"}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {cloudFileSize > 0 && (
                      <span className="text-[#5a7a9a] text-[10px]">{formatFileSize(cloudFileSize)}</span>
                    )}
                    <span className="flex items-center gap-1 text-emerald-400 text-[10px]">
                      <i className="ri-hard-drive-2-line" />
                      已写入本地内容
                    </span>
                  </div>
                  <p className="text-[#3a5a7a] text-[10px] mt-0.5 truncate">{bgMusic.url}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a
                    href={bgMusic.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#5a7a9a] hover:text-[#4fc3f7] hover:bg-[#4fc3f7]/10 transition-colors cursor-pointer"
                    title="在新标签页试听"
                  >
                    <i className="ri-external-link-line text-sm" />
                  </a>
                  <button
                    onClick={handleDeleteCloud}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] transition-colors cursor-pointer"
                    title="移除音乐链接"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5 flex flex-col items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d1529] border border-[#1e2d4a]">
                  <i className="ri-upload-cloud-2-line text-[#4fc3f7] text-2xl" />
                </div>
                <div className="text-center">
                  <p className="text-[#8aaec8] text-xs">拖拽音频文件到此处，或点击选择文件</p>
                  <p className="text-[#5a7a9a] text-[10px] mt-1">支持 MP3 / OGG / WAV / M4A / AAC / FLAC · 最大 50MB</p>
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {cloudError && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
              <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0" />
              <span className="text-red-400 text-xs">{cloudError}</span>
            </div>
          )}

          {/* Upload button */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={cloudUploadStatus === "uploading"}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] text-xs font-semibold hover:bg-[#4fc3f7]/25 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className={cloudUploadStatus === "uploading" ? "ri-loader-4-line animate-spin" : "ri-upload-cloud-2-line"} />
              {cloudUploadStatus === "uploading" ? "上传中..." : "选择文件上传"}
            </button>
            <p className="text-[#5a7a9a] text-xs">
              <i className="ri-information-line mr-1 text-emerald-400"></i>
              上传后写入内容数据，保存到服务端后访客可播放
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.ogg,.wav,.m4a,.aac,.flac"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleCloudUpload(file);
                e.target.value = "";
              }}
            />
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-[1px] bg-[#1e2d4a]" />
          <span className="text-[#3a5a7a] text-[10px]">或手动填写外链地址</span>
          <div className="flex-1 h-[1px] bg-[#1e2d4a]" />
        </div>

        {/* URL input */}
        <div className="flex flex-col gap-2">
          <label className="text-[#8aaec8] text-xs">音乐文件外链地址（MP3 / OGG / WAV / 直链 URL）</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/music.mp3"
              className="flex-1 min-w-0 bg-[#070c1a] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#4fc3f7] transition-colors placeholder-[#3a5a7a]"
            />
            <button
              onClick={handleApply}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${applied ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] hover:bg-[#4fc3f7]/25"}`}
            >
              {applied ? "已应用!" : "应用"}
            </button>
            {urlInput && (
              <button
                onClick={handleClear}
                className="px-3 py-2 rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] text-xs transition-colors cursor-pointer whitespace-nowrap"
              >
                清除
              </button>
            )}
          </div>
          {bgMusic.url && !isEmbeddedAudio && (
            <div className="flex items-center gap-2 px-3 py-2 bg-[#0a1628] border border-[#1a2840] rounded-lg">
              <i className="ri-music-line text-cyan-400 text-sm flex-shrink-0" />
              <span className="text-gray-400 text-xs truncate flex-1">{bgMusic.url}</span>
              <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] border border-emerald-500/20">使用中</span>
            </div>
          )}
          <p className="text-[#5a7a9a] text-xs">
            <i className="ri-information-line mr-1 text-[#4fc3f7]"></i>
            播放策略、音量和音乐文件修改后，记得点击「保存内容」让所有访客生效。
          </p>
        </div>
      </div>
    </div>
  );
}

function FriendLinksSection() {
  const { content, addFriendLink, removeFriendLink, updateFriendLink, updateSocialLink, updateFooterLegal } = useContentStore();
  const { friendLinks, socialLinks, footerLegal } = content;

  return (
    <div className="space-y-4">
      <BgMusicSection />
      {/* Social Media */}
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-share-line text-[#4fc3f7]"></i>
          社交媒体图标
          <span className="ml-auto text-[#5a7a9a] text-xs font-normal">可显示/隐藏每个图标</span>
        </p>
        <div className="space-y-3">
          {socialLinks.map((s: SocialLink, i: number) => (
            <div key={s.id} className={`bg-[#0a1628] border rounded-xl p-4 transition-all ${s.visible ? "border-[#1a2840]" : "border-[#1a2840] opacity-50"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className={`${s.icon} text-[#4fc3f7] text-base`} />
                  <span className="text-white text-xs font-medium">{s.label}</span>
                </div>
                <button
                  onClick={() => updateSocialLink(i, "visible", !s.visible)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${s.visible ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-[#1e2d4a] text-[#5a7a9a] border border-[#1e2d4a]"}`}
                >
                  <i className={s.visible ? "ri-eye-line" : "ri-eye-off-line"} />
                  {s.visible ? "显示中" : "已隐藏"}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <SocialIconPicker value={s.icon} onChange={(v) => updateSocialLink(i, "icon", v)} />
                <FieldInput
                  label="名称标签"
                  value={s.label}
                  onChange={(v) => updateSocialLink(i, "label", v)}
                  placeholder="如：微信"
                />
                <FieldInput
                  label="链接地址"
                  value={s.href}
                  onChange={(v) => updateSocialLink(i, "href", v)}
                  placeholder="如：https://... 或 mailto:..."
                />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[#5a7a9a] text-xs">
          <i className="ri-information-line mr-1 text-[#4fc3f7]"></i>
          点击「显示中/已隐藏」可控制每个图标是否出现在页脚。
        </p>
      </div>

      {/* Legal / Bottom Bar */}
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-file-shield-2-line text-[#4fc3f7]"></i>
          底栏版权与备案信息
        </p>
        <div className="space-y-3">
          <FieldInput
            label="版权文字"
            value={footerLegal.copyright}
            onChange={(v) => updateFooterLegal("copyright", v)}
            placeholder="如：© 2024 公司名称. All Rights Reserved"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldInput label="隐私政策（中文）" value={footerLegal.privacyZh} onChange={(v) => updateFooterLegal("privacyZh", v)} />
            <FieldInput label="隐私政策（英文）" value={footerLegal.privacyEn} onChange={(v) => updateFooterLegal("privacyEn", v)} />
            <FieldInput label="隐私政策链接" value={footerLegal.privacyHref} onChange={(v) => updateFooterLegal("privacyHref", v)} placeholder="#" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldInput label="服务条款（中文）" value={footerLegal.termsZh} onChange={(v) => updateFooterLegal("termsZh", v)} />
            <FieldInput label="服务条款（英文）" value={footerLegal.termsEn} onChange={(v) => updateFooterLegal("termsEn", v)} />
            <FieldInput label="服务条款链接" value={footerLegal.termsHref} onChange={(v) => updateFooterLegal("termsHref", v)} placeholder="#" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <FieldInput label="备案号（中文显示）" value={footerLegal.icpZh} onChange={(v) => updateFooterLegal("icpZh", v)} placeholder="如：京ICP备XXXXXXXX号" />
            <FieldInput label="备案号（英文显示）" value={footerLegal.icpEn} onChange={(v) => updateFooterLegal("icpEn", v)} placeholder="如：ICP License" />
            <FieldInput label="备案链接（可填工信部链接）" value={footerLegal.icpHref} onChange={(v) => updateFooterLegal("icpHref", v)} placeholder="https://beian.miit.gov.cn" />
          </div>
        </div>
      </div>

      {/* Friend Links */}
      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5">
        <p className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <i className="ri-links-line text-[#4fc3f7]"></i>
          友情链接管理
          <span className="ml-auto text-[#5a7a9a] text-xs font-normal">共 {friendLinks.length} 条</span>
        </p>
        <div className="space-y-3 mb-4">
          {friendLinks.map((link: FriendLink, i: number) => (
            <div key={i} className="bg-[#0a1628] border border-[#1a2840] rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[#5a7a9a] text-xs flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center rounded bg-[#4fc3f7]/10 text-[#4fc3f7] text-xs font-bold">{i + 1}</span>
                  链接 {i + 1}
                </span>
                <DeleteButton onClick={() => removeFriendLink(i)} disabled={friendLinks.length <= 1} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldInput label="显示名称" value={link.label} onChange={(v) => updateFriendLink(i, "label", v)} placeholder="如：中国航空工业集团" />
                <FieldInput label="链接地址" value={link.href} onChange={(v) => updateFriendLink(i, "href", v)} placeholder="如：https://www.example.com" />
              </div>
            </div>
          ))}
        </div>
        <AddButton label="添加友情链接" onClick={addFriendLink} />
      </div>
    </div>
  );
}

// ─── Main ContentEditor ────────────────────────────────────────────────────

function LegacyContentEditor() {
  const { reset, saveToCloud, content, loading } = useContentStore();
  const [section, setSection] = useState<Section>("banner");
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [exported, setExported] = useState(false);
  const [apiStatus, setApiStatus] = useState<"checking" | "online" | "static">("checking");

  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.health, { headers: { Accept: "application/json" } })
      .then(async (res) => {
        const type = res.headers.get("content-type") || "";
        if (!cancelled) setApiStatus(res.ok && type.includes("application/json") ? "online" : "static");
      })
      .catch(() => {
        if (!cancelled) setApiStatus("static");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(false);
    setSaveErrorMessage("");
    const result = await saveToCloud(content);
    forceBroadcastUpdate();
    setSaving(false);
    if (result.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } else {
      setSaveError(true);
      setSaveErrorMessage(result.error || "未连接服务端，仅本地暂存");
      setTimeout(() => setSaveError(false), 5000);
    }
  };

  const handleExport = () => {
    const content = getContent();
    const json = JSON.stringify(content, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `site-content-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setExported(true);
    setTimeout(() => setExported(false), 3000);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <i className="ri-loader-4-line text-4xl text-[#4fc3f7] animate-spin" />
        <p className="text-[#8aaec8] text-sm">正在加载最新内容...</p>
      </div>
    );
  }



  return (
    <div className="space-y-6">
      <div className="space-y-6 min-w-0">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <SectionTab label="导航栏" icon="ri-navigation-line" active={section === "nav"} onClick={() => setSection("nav")} />
          <SectionTab label="Banner" icon="ri-slideshow-line" active={section === "banner"} onClick={() => setSection("banner")} />
          <SectionTab label="关于我们" icon="ri-building-2-line" active={section === "about"} onClick={() => setSection("about")} />
          <SectionTab label="产品与服务" icon="ri-apps-line" active={section === "products"} onClick={() => setSection("products")} />
          <SectionTab label="联系我们" icon="ri-customer-service-2-line" active={section === "contact"} onClick={() => setSection("contact")} />
          <SectionTab label="产品详情" icon="ri-box-3-line" active={section === "productsDetail"} onClick={() => setSection("productsDetail")} />
          <SectionTab label="新闻发布" icon="ri-newspaper-line" active={section === "news"} onClick={() => setSection("news")} />
          <SectionTab label="友情链接" icon="ri-links-line" active={section === "friendLinks"} onClick={() => setSection("friendLinks")} />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (window.confirm("确认恢复所有内容（含图片）为默认值？")) reset();
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-restart-line"></i>
            恢复默认
          </button>
          <button
            onClick={handleExport}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap border ${
              exported
                ? "bg-amber-500/20 text-amber-400 border-amber-500/40"
                : "text-[#5a7a9a] hover:text-amber-400 border-[#1e2d4a] hover:border-amber-500/40"
            }`}
          >
            <i className={exported ? "ri-check-line" : "ri-download-2-line"}></i>
            {exported ? "已导出!" : "导出内容"}
          </button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-[#5a7a9a] hover:text-[#4fc3f7] border border-[#1e2d4a] hover:border-[#4fc3f7]/40 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line"></i>
            预览主页
          </a>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
              saved
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                : saveError
                ? "bg-red-500/20 text-red-400 border border-red-500/40"
                : saving
                ? "bg-[#4fc3f7]/50 text-[#070c1a] cursor-not-allowed"
                : "bg-[#4fc3f7] text-[#070c1a] hover:bg-[#0288d1]"
            }`}
          >
            <i className={saved ? "ri-check-double-line" : saveError ? "ri-error-warning-line" : saving ? "ri-loader-4-line animate-spin" : "ri-save-line"}></i>
            {saved ? "已保存" : saveError ? "未连接服务端，仅本地暂存" : saving ? "保存中..." : "保存内容"}
          </button>
        </div>
      </div>

      {/* Tip */}
      <div className="flex flex-col gap-2 bg-[#4fc3f7]/5 border border-[#4fc3f7]/20 rounded-lg px-4 py-3">
        <div className="flex items-start gap-2">
          <i className="ri-information-line text-[#4fc3f7] text-sm flex-shrink-0 mt-0.5"></i>
          <p className="text-[#8aaec8] text-xs">
            修改内容会<strong className="text-[#4fc3f7]">自动实时暂存到浏览器</strong>。点击「保存内容」后，若当前站点已正确部署 PHP + MySQL 接口，内容会写入服务器数据库并对所有访客生效；若当前只是静态预览，则只能在本浏览器临时预览和导出内容。
          </p>
        </div>
        <div className={`flex items-start gap-2 rounded-lg px-3 py-2 border ${apiStatus === "online" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20"}`}>
          <i className={`${apiStatus === "online" ? "ri-cloud-line text-emerald-400" : "ri-computer-line text-amber-400"} text-sm flex-shrink-0 mt-0.5`}></i>
          <p className="text-[#8aaec8] text-xs">
            {apiStatus === "checking" && <strong className="text-[#4fc3f7]">正在检测内容保存接口...</strong>}
            {apiStatus === "online" && <><strong className="text-emerald-400">服务端数据库已连接</strong>，保存后前台、产品页、详情页、AI 客服配置和页脚都会读取最新内容。</>}
            {apiStatus === "static" && <><strong className="text-amber-400">当前未连接 PHP 保存接口</strong>，请确认服务器已部署 `api/`、`config/` 和 MySQL 数据库后再保存到正式环境。</>}
          </p>
        </div>
        {saveErrorMessage && (
          <div className="flex items-start gap-2 rounded-lg px-3 py-2 border bg-red-500/5 border-red-500/20">
            <i className="ri-close-circle-line text-red-400 text-sm flex-shrink-0 mt-0.5"></i>
            <p className="text-red-300 text-xs break-all">最近一次保存失败：{saveErrorMessage}</p>
          </div>
        )}
      </div>

      <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#4fc3f7]/10 text-[#4fc3f7] flex items-center justify-center flex-shrink-0">
          <i className="ri-node-tree" />
        </div>
        <div>
          <h3 className="text-white text-sm font-semibold mb-1">当前编辑影响范围</h3>
          <p className="text-[#8aaec8] text-xs leading-5">{sectionHelp[section]}</p>
        </div>
      </div>

      {/* Sections */}
      {section === "nav" && <NavSection />}
      {section === "banner" && <BannerSection />}
      {section === "about" && <AboutSection />}
      {section === "products" && <ProductsSection />}
      {section === "contact" && <ContactSection />}
      {section === "productsDetail" && <ProductDetailEditorSection />}
      {section === "news" && <NewsArticlesSection />}
      {section === "friendLinks" && <FriendLinksSection />}
      </div>
    </div>
  );
}

type StableSection = "home" | "about" | "products" | "contact" | "footer" | "details";

const stableTabs: Array<{ id: StableSection; label: string; icon: string; preview: string }> = [
  { id: "home", label: "首页与导航", icon: "ri-home-4-line", preview: "/#banner" },
  { id: "about", label: "关于我们", icon: "ri-building-2-line", preview: "/#about" },
  { id: "products", label: "产品服务", icon: "ri-apps-line", preview: "/#products" },
  { id: "contact", label: "联系我们", icon: "ri-customer-service-2-line", preview: "/#contact" },
  { id: "footer", label: "页脚信息", icon: "ri-layout-bottom-line", preview: "/#footer" },
  { id: "details", label: "产品详情", icon: "ri-box-3-line", preview: "/products" },
];

function StableCard({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-5 space-y-4">
      <h3 className="text-white text-sm font-semibold flex items-center gap-2">
        <i className={`${icon} text-[#4fc3f7]`} />
        {title}
      </h3>
      {children}
    </div>
  );
}

function JsonEditor({ label, value, onChange }: { label: string; value: unknown; onChange: (value: unknown) => void }) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState("");

  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
  }, [value]);

  const apply = () => {
    try {
      onChange(JSON.parse(text));
      setError("");
    } catch {
      setError("JSON 格式不正确，请检查括号、逗号和引号");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label className="text-[#8aaec8] text-xs">{label}</label>
        <button onClick={apply} className="px-3 py-1.5 rounded-lg bg-[#4fc3f7]/15 border border-[#4fc3f7]/30 text-[#4fc3f7] text-xs font-semibold hover:bg-[#4fc3f7]/25 transition-colors cursor-pointer whitespace-nowrap">应用 JSON</button>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} className="w-full bg-[#070c1a] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-xs font-mono resize-y focus:outline-none focus:border-[#4fc3f7]" />
      {error && <p className="text-red-400 text-xs">{error}</p>}
    </div>
  );
}

function StableJsonContentEditor() {
  const store = useContentStore();
  const { content, loading, update, reset, saveToCloud } = store;
  const [section, setSection] = useState<StableSection>("home");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [apiStatus, setApiStatus] = useState<"checking" | "online" | "static">("checking");

  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.health, { headers: { Accept: "application/json" } })
      .then((res) => {
        const type = res.headers.get("content-type") || "";
        if (!cancelled) setApiStatus(res.ok && type.includes("application/json") ? "online" : "static");
      })
      .catch(() => {
        if (!cancelled) setApiStatus("static");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeTab = stableTabs.find((tab) => tab.id === section) ?? stableTabs[0];

  const save = async () => {
    setSaving(true);
    setSaveError(false);
    const ok = await saveToCloud(content);
    forceBroadcastUpdate();
    setSaving(false);
    if (ok) {
      setSaved(true);
      setPreviewKey((v) => v + 1);
      setTimeout(() => setSaved(false), 2500);
    } else {
      setSaveError(true);
      setTimeout(() => setSaveError(false), 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <i className="ri-loader-4-line text-4xl text-[#4fc3f7] animate-spin" />
        <p className="text-[#8aaec8] text-sm">正在加载内容管理...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {stableTabs.map((tab) => (
            <SectionTab key={tab.id} label={tab.label} icon={tab.icon} active={section === tab.id} onClick={() => setSection(tab.id)} />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => { if (window.confirm("确认恢复默认内容？")) reset(); }} className="px-3 py-2 rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] text-sm transition-colors cursor-pointer whitespace-nowrap">恢复默认</button>
          <button onClick={save} disabled={saving} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${saved ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : saveError ? "bg-red-500/20 text-red-400 border border-red-500/40" : "bg-[#4fc3f7] text-[#070c1a] hover:bg-[#0288d1]"}`}>
            {saving ? "保存中..." : saved ? "已保存" : saveError ? "服务端未连接" : "保存内容"}
          </button>
        </div>
      </div>

      <div className={`rounded-xl border px-4 py-3 text-xs ${apiStatus === "online" ? "bg-emerald-500/5 border-emerald-500/20 text-emerald-300" : "bg-amber-500/5 border-amber-500/20 text-amber-300"}`}>
        {apiStatus === "checking" && "正在检测保存接口..."}
        {apiStatus === "online" && "服务端数据库已连接，保存后所有访客可见。"}
        {apiStatus === "static" && "当前为静态托管模式，只能本地预览；请使用 Node.js 运行 npm start 才能保存给所有访客。"}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_430px] gap-5 items-start">
        <div className="space-y-5 min-w-0">
          {section === "home" && (
            <>
              <StableCard title="导航栏" icon="ri-navigation-line">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldInput label="Logo 图片地址" value={content.nav.logoImage} onChange={(v) => update((prev) => ({ ...prev, nav: { ...prev.nav, logoImage: v } }))} />
                  <FieldInput label="Logo 描述" value={content.nav.logoAlt} onChange={(v) => update((prev) => ({ ...prev, nav: { ...prev.nav, logoAlt: v } }))} />
                </div>
                <JsonEditor label="导航菜单 JSON" value={content.nav.items} onChange={(items) => update((prev) => ({ ...prev, nav: { ...prev.nav, items: items as typeof prev.nav.items } }))} />
              </StableCard>
              <StableCard title="首页 Banner" icon="ri-slideshow-line">
                <JsonEditor label="轮播内容 JSON" value={content.bannerSlides} onChange={(bannerSlides) => update((prev) => ({ ...prev, bannerSlides: bannerSlides as typeof prev.bannerSlides }))} />
              </StableCard>
            </>
          )}

          {section === "about" && (
            <StableCard title="关于我们" icon="ri-building-2-line">
              <FieldInput label="中文介绍第一段" value={content.aboutZhDesc1} onChange={(v) => update((prev) => ({ ...prev, aboutZhDesc1: v }))} multiline />
              <FieldInput label="中文介绍第二段" value={content.aboutZhDesc2} onChange={(v) => update((prev) => ({ ...prev, aboutZhDesc2: v }))} multiline />
              <FieldInput label="英文介绍第一段" value={content.aboutEnDesc1} onChange={(v) => update((prev) => ({ ...prev, aboutEnDesc1: v }))} multiline />
              <FieldInput label="英文介绍第二段" value={content.aboutEnDesc2} onChange={(v) => update((prev) => ({ ...prev, aboutEnDesc2: v }))} multiline />
              <FieldInput label="关于我们图片地址" value={content.aboutImage} onChange={(v) => update((prev) => ({ ...prev, aboutImage: v }))} />
              <JsonEditor label="能力数据 JSON" value={content.aboutStats} onChange={(aboutStats) => update((prev) => ({ ...prev, aboutStats: aboutStats as typeof prev.aboutStats }))} />
            </StableCard>
          )}

          {section === "products" && (
            <StableCard title="首页产品服务" icon="ri-apps-line">
              <JsonEditor label="产品服务卡片 JSON" value={content.products} onChange={(products) => update((prev) => ({ ...prev, products: products as typeof prev.products }))} />
            </StableCard>
          )}

          {section === "contact" && (
            <StableCard title="联系我们" icon="ri-customer-service-2-line">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldInput label="中文标题" value={content.contact.zhTitle} onChange={(v) => update((prev) => ({ ...prev, contact: { ...prev.contact, zhTitle: v } }))} />
                <FieldInput label="英文标题" value={content.contact.enTitle} onChange={(v) => update((prev) => ({ ...prev, contact: { ...prev.contact, enTitle: v } }))} />
              </div>
              <FieldInput label="中文副标题" value={content.contact.zhSubtitle} onChange={(v) => update((prev) => ({ ...prev, contact: { ...prev.contact, zhSubtitle: v } }))} multiline />
              <FieldInput label="英文副标题" value={content.contact.enSubtitle} onChange={(v) => update((prev) => ({ ...prev, contact: { ...prev.contact, enSubtitle: v } }))} multiline />
              <FieldInput label="地图嵌入地址" value={content.contact.mapEmbedUrl} onChange={(v) => update((prev) => ({ ...prev, contact: { ...prev.contact, mapEmbedUrl: v } }))} />
              <JsonEditor label="联系方式 JSON" value={content.contact.infoItems} onChange={(infoItems) => update((prev) => ({ ...prev, contact: { ...prev.contact, infoItems: infoItems as typeof prev.contact.infoItems } }))} />
            </StableCard>
          )}

          {section === "footer" && (
            <StableCard title="页脚、友情链接、社交图标" icon="ri-layout-bottom-line">
              <FieldInput label="版权文字" value={content.footerLegal.copyright} onChange={(v) => update((prev) => ({ ...prev, footerLegal: { ...prev.footerLegal, copyright: v } }))} />
              <JsonEditor label="友情链接 JSON" value={content.friendLinks} onChange={(friendLinks) => update((prev) => ({ ...prev, friendLinks: friendLinks as typeof prev.friendLinks }))} />
              <JsonEditor label="社交图标 JSON" value={content.socialLinks} onChange={(socialLinks) => update((prev) => ({ ...prev, socialLinks: socialLinks as typeof prev.socialLinks }))} />
              <JsonEditor label="版权备案 JSON" value={content.footerLegal} onChange={(footerLegal) => update((prev) => ({ ...prev, footerLegal: footerLegal as typeof prev.footerLegal }))} />
            </StableCard>
          )}

          {section === "details" && (
            <StableCard title="产品详情页数据" icon="ri-box-3-line">
              <JsonEditor label="产品分类 JSON" value={content.productCategories} onChange={(productCategories) => update((prev) => ({ ...prev, productCategories: productCategories as typeof prev.productCategories }))} />
              <JsonEditor label="产品详情 JSON" value={content.productsDetail} onChange={(productsDetail) => update((prev) => ({ ...prev, productsDetail: productsDetail as typeof prev.productsDetail }))} />
            </StableCard>
          )}
        </div>

        <aside className="xl:sticky xl:top-6 space-y-4">
          <StableCard title="前台可视化预览" icon="ri-eye-line">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[#8aaec8] text-xs">当前预览：{activeTab.label}</p>
              <button onClick={() => setPreviewKey((v) => v + 1)} className="px-3 py-1.5 rounded-lg text-xs text-[#8aaec8] hover:text-[#4fc3f7] border border-[#1e2d4a] hover:border-[#4fc3f7]/40 transition-colors cursor-pointer whitespace-nowrap">刷新预览</button>
            </div>
            <iframe key={`${activeTab.id}-${previewKey}`} src={activeTab.preview} title="前台可视化预览" className="w-full h-[560px] rounded-lg border border-[#1e2d4a] bg-white" />
          </StableCard>
          <StableCard title="功能关联审核" icon="ri-checkbox-circle-line">
            <div className="space-y-2 text-xs text-[#8aaec8] leading-5">
              <p><span className="text-emerald-400">已关联</span>：导航、Banner、关于我们、产品服务、联系模块、页脚。</p>
              <p><span className="text-emerald-400">已关联</span>：产品列表页和产品详情页读取产品详情数据。</p>
              <p><span className={apiStatus === "online" ? "text-emerald-400" : "text-amber-400"}>{apiStatus === "online" ? "可保存" : "静态模式"}</span>：内容接口 `/api/content`。</p>
            </div>
          </StableCard>
        </aside>
      </div>
    </div>
  );
}

export default LegacyContentEditor;
